/**
 * 账号能力契约（统一登录 + 统一积分）。
 *
 * 权威边界：
 * - 身份：由 YoGo 账号中心（account.yogo.wang）统一负责，本站只做 BFF 换票与本地会话；
 * - 积分余额/流水：同样由账号中心记账，本站只代查询，**永不**在前端计算或修改余额。
 */

export type AccountStatus = 'pending' | 'active' | 'disabled';
export type AccountRole = 'user' | 'admin';

export interface AccountUser {
  id: string;
  displayName: string;
  email: string;
  status: AccountStatus;
  role: AccountRole;
  /** 是否已与账号中心账号关联（关联后才有积分可查） */
  linked: boolean;
}

export interface CreditBalance {
  balance: number;
  currency: 'credit';
  dailyPoints: number;
  bonusPoints: number;
  /** 每日额度配置（来自账号中心策略） */
  dailyGrantQuota: number;
  /** 当日日期（Asia/Shanghai） */
  day: string;
  note?: string;
}

export interface CreditQuote {
  cost: number;
  reasonCode: string;
  idempotencyKey: string;
}

export interface CreditChargeResult {
  transactionId: string;
  charged: number;
  balanceAfter: number;
}

export interface AccountSession {
  authenticated: boolean;
  user: AccountUser | null;
}

export interface QuoteInput {
  operation: string;
  sourceSize?: number;
  sourceFormat?: string;
  targetFormat?: string;
}

export type CreditErrorCode =
  | 'NOT_AUTHENTICATED'
  | 'INSUFFICIENT_CREDITS'
  | 'NOT_IMPLEMENTED'
  | 'UNAVAILABLE'
  | 'NETWORK'
  | 'UNKNOWN';

export class CreditError extends Error {
  readonly code: CreditErrorCode;

  constructor(code: CreditErrorCode, message: string) {
    super(message);
    this.name = 'CreditError';
    this.code = code;
  }
}

export class AccountApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly startUrl?: string;

  constructor(code: string, message: string, status: number, startUrl?: string) {
    super(message);
    this.name = 'AccountApiError';
    this.code = code;
    this.status = status;
    this.startUrl = startUrl;
  }
}

export interface AccountProvider {
  getSession(): Promise<AccountSession>;
  getCredits(): Promise<CreditBalance>;
  /**
   * 发起登录：跳转到账号中心（BFF 模式，本站不处理密码）。
   * 浏览器环境是整页导航（不会返回）；测试环境注入导航函数即可断言。
   */
  signIn(options?: { returnTo?: string }): void;
  signOut(): Promise<void>;
  /** 云端转换报价：需要服务端 /api/conversions/* 编排（本轮未开放），未实现时抛 NOT_IMPLEMENTED。 */
  quoteCredits(input: QuoteInput): Promise<CreditQuote>;
  chargeCredits(quote: CreditQuote): Promise<CreditChargeResult>;
  refundCredits(input: { transactionId: string; reason: string; idempotencyKey: string }): Promise<void>;
}
